import React, { useState, useEffect } from 'react';
import { Loader2, RefreshCw, Search, XCircle } from 'lucide-react';
import api from '../../utils/api';

const STATUS_OPTIONS = [
  { value: 'all', label: '全部状态' },
  { value: 'pending', label: '待生效' },
  { value: 'active', label: '已开通' },
  { value: 'cancelled', label: '已取消' }
];

// 默认展示「已开通」的签约订单
const DEFAULT_STATUS = 'active';

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) {
    return String(value).slice(0, 19).replace('T', ' ');
  }
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// 支付方式文案与退款管理页保持一致（取签约父单订单的 pay_type）
const getPayTypeLabel = (payType) => {
  if (payType === 'ly360') return '360支付';
  if (payType === 'ly360top') return '360支付(top)';
  return payType || '-';
};

export default function AutopayManagementTab() {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [filterStatus, setFilterStatus] = useState(DEFAULT_STATUS);
  const [searchInput, setSearchInput] = useState('');
  const [filterKeyword, setFilterKeyword] = useState('');
  const [jumpPage, setJumpPage] = useState('');
  const [cancelDialog, setCancelDialog] = useState({ open: false, agreement: null });
  const [cancelling, setCancelling] = useState(false);
  const [notice, setNotice] = useState(null); // { type: 'success' | 'error', text }

  const fetchAgreements = async ({ page = 1, status = filterStatus, keyword = filterKeyword } = {}) => {
    setLoading(true);
    try {
      const params = { page, limit: pagination.limit };
      if (status !== 'all') params.status = status;
      if (keyword) params.keyword = keyword;
      const res = await api.get('/admin/autopay/agreements', { params });
      const data = res.data?.data || res.data || {};
      setAgreements(data.list || []);
      setPagination((prev) => ({
        ...prev,
        page: data.pagination?.page || page,
        total: data.pagination?.total || 0,
        totalPages: Math.max(1, data.pagination?.totalPages || 1)
      }));
    } catch (err) {
      console.error('获取自动续费签约列表失败:', err);
      setAgreements([]);
      setNotice({ type: 'error', text: err.response?.data?.error || '获取自动续费签约列表失败' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgreements({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterKeyword]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), 8000);
    return () => clearTimeout(timer);
  }, [notice]);

  const handleSearch = () => {
    setFilterKeyword(searchInput.trim());
  };

  const handleReset = () => {
    setSearchInput('');
    setFilterKeyword('');
    setFilterStatus(DEFAULT_STATUS);
  };

  const hasActiveFilters = filterStatus !== DEFAULT_STATUS || filterKeyword !== '';

  const handleJump = () => {
    const target = parseInt(jumpPage, 10);
    if (Number.isFinite(target) && target >= 1 && target <= pagination.totalPages) {
      fetchAgreements({ page: target });
      setJumpPage('');
    }
  };

  const openCancelDialog = (agreement) => {
    setCancelDialog({ open: true, agreement });
  };

  const closeCancelDialog = () => {
    if (cancelling) return;
    setCancelDialog({ open: false, agreement: null });
  };

  const handleCancelConfirm = async () => {
    const agreement = cancelDialog.agreement;
    if (!agreement) return;
    setCancelling(true);
    try {
      const res = await api.post('/admin/autopay/cancel', { agreementId: agreement.id });
      setNotice({ type: 'success', text: res.data?.message || '已取消自动续费' });
      setCancelDialog({ open: false, agreement: null });
      fetchAgreements({ page: pagination.page });
    } catch (err) {
      setNotice({ type: 'error', text: err.response?.data?.error || '取消自动续费失败，请稍后重试' });
    } finally {
      setCancelling(false);
    }
  };

  const renderStatusBadge = (status) => {
    if (status === 'active') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
          已开通
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
          待生效
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
        已取消
      </span>
    );
  };

  const renderAmount = (row) => {
    if (row.auto_pay_amount == null) return '-';
    return (
      <div>
        <div className="text-gray-900 dark:text-white font-medium">¥{(row.auto_pay_amount / 100).toFixed(2)}</div>
        <div className="text-xs text-gray-400">每{row.period_text || '-'}</div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">自动续费管理</h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              查看 360 自动续费签约与下次扣款日；「取消自动续费」将向 360 发起服务端解约，成功后立即停止后续代扣。解约失败会提示原因且不做本地标记；若 360 侧仍发生异常扣款，系统将自动拦截不发货并生成待人工退款订单（订单名带「已取消拦截」标识）。
            </p>
          </div>
          <button
            onClick={() => fetchAgreements({ page: pagination.page })}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            刷新
          </button>
        </div>

        {notice && (
          <div
            className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
              notice.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300'
            }`}
          >
            {notice.text}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="手机号 / 昵称 / 签约单号 / 协议号"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            onClick={handleSearch}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
          >
            搜索
          </button>
          {hasActiveFilters && (
            <button
              onClick={handleReset}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              重置
            </button>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">用户</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">套餐</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">支付方式</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">状态</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">每期扣款</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">首扣日</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">下次扣款日</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">最近扣款</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">协议号</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    加载中...
                  </td>
                </tr>
              ) : agreements.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-sm text-gray-400">
                    暂无自动续费签约记录
                  </td>
                </tr>
              ) : (
                agreements.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div className="text-gray-900 dark:text-white font-medium">{row.user_label}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{row.phone || '-'} · ID:{row.user_id}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                      {row.plan_name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                      {getPayTypeLabel(row.pay_type)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {renderStatusBadge(row.status)}
                      {row.status === 'cancelled' && row.cancelled_at && (
                        <div className="mt-1 text-xs text-gray-400">{formatDateTime(row.cancelled_at)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">{renderAmount(row)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                      {row.execute_time || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className="text-gray-900 dark:text-white font-medium">{row.next_charge_at || '-'}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {row.last_charge_order_no ? (
                        <div>
                          <div className="text-xs text-gray-700 dark:text-gray-200 break-all">{row.last_charge_order_no}</div>
                          <div className="text-xs text-gray-400">{formatDateTime(row.last_charge_at)}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {row.agreement_number ? (
                        <span className="text-xs text-gray-600 dark:text-gray-300 break-all">{row.agreement_number}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {row.status === 'pending' || row.status === 'active' ? (
                        <button
                          onClick={() => openCancelDialog(row)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 dark:border-red-700 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          取消自动续费
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm text-gray-500">共 {pagination.total} 条记录</span>
            <div className="flex gap-2">
              <input
                type="number"
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                placeholder="页码"
                min={1}
                max={pagination.totalPages}
                className="w-16 px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
              <button
                onClick={handleJump}
                className="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-md text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                跳转
              </button>
              <button
                onClick={() => fetchAgreements({ page: pagination.page - 1 })}
                disabled={pagination.page === 1 || loading}
                className="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-md text-sm disabled:opacity-50"
              >
                上一页
              </button>
              <span className="px-3 py-1 text-sm">
                {pagination.page} / {pagination.totalPages}
              </span>
              <button
                onClick={() => fetchAgreements({ page: pagination.page + 1 })}
                disabled={pagination.page === pagination.totalPages || loading}
                className="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-md text-sm disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {cancelDialog.open && cancelDialog.agreement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-xl bg-white dark:bg-gray-800 p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">确认取消自动续费</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              确认后将向 360 发起服务端解约（真正停止后续代扣），解约成功即刻生效；若解约失败（如协议号无效）会提示原因，不会标记为已取消。
            </p>

            <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-4 text-sm text-gray-700 dark:text-gray-200 space-y-1">
              <div>用户：{cancelDialog.agreement.user_label}（ID:{cancelDialog.agreement.user_id}）</div>
              <div>手机号：{cancelDialog.agreement.phone || '-'}</div>
              <div>套餐：{cancelDialog.agreement.plan_name}</div>
              <div>支付方式：{getPayTypeLabel(cancelDialog.agreement.pay_type)}</div>
              <div>签约单号：{cancelDialog.agreement.sign_order_no}</div>
              <div>下次扣款日：{cancelDialog.agreement.next_charge_at || '-'}</div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeCancelDialog}
                disabled={cancelling}
                className="rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                再想想
              </button>
              <button
                type="button"
                onClick={handleCancelConfirm}
                disabled={cancelling}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50"
              >
                {cancelling ? '处理中...' : '确认取消自动续费'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
